import { ComputedRef, reactive, ref, unref, watchEffect } from 'vue';
import type { Recordable } from '@mts-fe/types';

export type SymbolKey = '.' | '' | '-';

export interface VirtualKeyboardProps {
  /** 键盘符号，默认. */
  symbol?: SymbolKey;
}
type CloseBy = 'ok' | 'outside';
interface KeyboardState {
  show: boolean;
  activeInput: HTMLInputElement | null;
  config: VirtualKeyboardProps;
  onInput?: (newValue: string, key: string) => void;
  onDelete?: (newValue: string) => void;
  onClose?: (newValue: string, by: CloseBy) => void;
}
export const keyboardState = reactive<KeyboardState>({
  show: false,
  activeInput: null,
  config: { symbol: '.' },
});

/** 激活全局的虚拟键盘 */
export const useKeyboard = (
  inputRef: KeyboardState['activeInput'],
  config: VirtualKeyboardProps = {},
  callbacks: Pick<KeyboardState, 'onInput' | 'onDelete' | 'onClose'> = {},
) => {
  Object.assign(keyboardState, {
    show: true,
    activeInput: inputRef,
    config: { symbol: '.', ...config },
    onInput: callbacks.onInput,
    onDelete: callbacks.onDelete,
    onClose: callbacks.onClose,
  });
};

/** 全局虚拟键盘事件处理器 */
export const keyboardHandler = {
  onKeydown: (key: string | SymbolKey) => {
    const input = keyboardState.activeInput;
    if (!input) return;

    const currentValue = input.value || '';
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    const newValue =
      currentValue.substring(0, selectionStart) +
      key +
      currentValue.substring(selectionStart === selectionEnd ? selectionStart : selectionEnd);

    input.value = newValue;
    const cursorPos = selectionStart + key.length;
    input.setSelectionRange(cursorPos, cursorPos);
    keyboardState.onInput?.(newValue, key);
  },
  onDelete: () => {
    const input = keyboardState.activeInput;
    if (!input) return;

    const currentValue = input.value || '';
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    let newValue = currentValue;
    let cursorPos = selectionStart;
    if (selectionStart === selectionEnd) {
      if (selectionStart > 0) {
        newValue =
          currentValue.substring(0, selectionStart - 1) + currentValue.substring(selectionStart);
        cursorPos = selectionStart - 1;
      }
    } else {
      newValue = currentValue.substring(0, selectionStart) + currentValue.substring(selectionEnd);
      cursorPos = selectionStart;
    }

    input.value = newValue;
    input.setSelectionRange(cursorPos, cursorPos);
    keyboardState.onDelete?.(newValue);
  },
  onClose: (by: CloseBy) => {
    const input = keyboardState.activeInput;
    if (!input) return;

    keyboardState.onClose?.(input.value, by);
    keyboardState.show = false;
    keyboardState.activeInput = null;
  },
};

export interface KeyboardValueProps {
  /** 最小值，默认0 */
  min?: number;
  /** 最大值 */
  max?: number;
  /** 小数位数 */
  precision?: number;
}

export const useKeyboardValue = <T extends Recordable>(
  props: T,
  key: keyof T = 'value',
  options: {
    valueProps?: ComputedRef<KeyboardValueProps> | KeyboardValueProps;
    onChange?: (value: string) => void;
    onOk?: (value: string) => void;
    onClose?: (value: string) => void;
  } = {},
) => {
  const formatValue = (value: unknown): string => (value ?? '').toString();
  const inputValue = ref(formatValue(props[key]));
  watchEffect(() => {
    inputValue.value = formatValue(props[key]);
  });

  const { valueProps = {}, onChange, onOk, onClose } = options;
  const updateValue = (val: string) => {
    const { min = 0, max } = unref(valueProps);
    if ((min || min === 0) && val && Number(val) < +min) {
      val = min.toString();
    }
    if (typeof max === 'number' && Number(val) > +max) {
      val = max.toString();
    }

    inputValue.value = val;
    onChange?.(inputValue.value);
  };

  const handleKeydown = (num: string | SymbolKey) => {
    const { precision } = unref(valueProps);
    let newVal = inputValue.value === '0' ? '' : inputValue.value;

    if ((num === '.' || num === '-') && newVal.includes(num)) {
      return;
    }
    // 精度控制检查
    if (precision && newVal.includes('.') && newVal.split('.')[1].length >= precision) {
      return;
    }
    newVal += num;

    // 自动补零
    if (newVal === '.') {
      newVal = '0' + newVal;
    }
    updateValue(newVal);
  };

  const eventsHandler = {
    onKeydown: handleKeydown,
    onDelete: () => {
      updateValue(inputValue.value.slice(0, inputValue.value.length - 1));
    },
    onOk: () => {
      onOk?.(inputValue.value);
    },
    onClose: () => {
      onClose?.(inputValue.value);
    },
  };

  return { inputValue, updateValue, eventsHandler };
};
